import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, FileOutput, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const BASIS = ["HOURLY", "DAILY", "MONTHLY"];
const WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT"];
const ACCOUNTS_ROLES = ["ORG_ADMIN", "ACCOUNTS"];

export default function HireOrders() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const canBill = ACCOUNTS_ROLES.includes(user?.role);
  const { companies } = useCompany() || {};
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [projects, setProjects] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: o }, { data: v }, { data: eq }, { data: pr }] = await Promise.all([
        api.get("/hire-orders"), api.get("/procurement/vendors"), api.get("/equipment"), api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setOrders(o);
      setProjects(pr || []); setVendors(v); setEquipment(eq);
      api.get("/masters/tax-codes").then(({ data }) => setTaxCodes((data || []).filter((t) => t.is_active !== false))).catch(() => {});
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || "—";
  const equipmentName = (id) => equipment.find((e) => e.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="hire-orders-page">
      <PageHeader title="Hire Orders" description="Hired equipment agreements. Generate a bill directly from logbook hours for a date range." action={canWrite && <CreateHireOrderDialog companies={companies} vendors={vendors} equipment={equipment} projects={projects} taxCodes={taxCodes} onCreated={load} />} />

      <div className="rounded-xl border border-border bg-card">
        {!loading && orders.length === 0 ? (
          <EmptyState title="No hire orders yet" testId="hire-orders-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Number</TableHead><TableHead>Vendor</TableHead><TableHead>Project</TableHead><TableHead>Equipment</TableHead><TableHead className="text-right">Rate</TableHead><TableHead>Basis</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} data-testid={`hire-order-row-${o.number}`}>
                  <TableCell className="font-mono text-xs text-foreground">{o.number}</TableCell>
                  <TableCell className="text-foreground">{vendorName(o.vendor_id)}</TableCell>
                  <TableCell className="text-xs">{projects.find((p) => p.id === o.project_id)?.code || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{o.equipment_id ? equipmentName(o.equipment_id) : o.equipment_description}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-foreground">{fmtNum(o.rate, 2)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{o.basis}</TableCell>
                  <TableCell><StatusPill value={o.status} />{o.closed_on && <div className="text-[10px] text-muted-foreground">closed {o.closed_on}</div>}{!o.closed_on && o.end_date && <div className="text-[10px] text-muted-foreground">till {o.end_date}</div>}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {canBill && (o.equipment_id || o.basis === "MONTHLY") && <GenerateBillDialog order={o} />}
                    {canBill && o.status !== "CLOSED" && (
                      <Button size="sm" variant="ghost" title="Close hire (machine returned)" onClick={async () => {
                        const d = window.prompt("Date the machine was returned (YYYY-MM-DD):", todayISO());
                        if (!d) return;
                        try { await api.post(`/hire-orders/${o.id}/close`, { closed_on: d }); toast.success(`${o.number} closed — machine back in the yard`); load(); }
                        catch (err) { toast.error(getErrorMessage(err)); }
                      }} data-testid={`close-hire-order-${o.number}`}><Lock className="h-3.5 w-3.5" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function GenerateBillDialog({ order }) {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState({ date_from: order.start_date, date_to: todayISO() });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/hire-orders/${order.id}/generate-bill`, range);
      toast.success(`Draft purchase bill ${data.number}: ${fmtNum(data.total_amount, 2)} (incl. GST ${fmtNum(data.tax_amount, 2)}) — add the vendor bill no. and post it in Purchase Bills`);
      setOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`generate-hire-bill-button-${order.number}`}><FileOutput className="h-3.5 w-3.5" /> Generate bill</Button></DialogTrigger>
      <DialogContent className="sm:max-w-sm" data-testid={`generate-hire-bill-dialog-${order.number}`}>
        <DialogHeader><DialogTitle>Generate hire bill</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="generate-hire-bill-form">
          <div><Label>From</Label><Input required type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} className="mt-1.5" data-testid="hire-bill-date-from-input" /></div>
          <div><Label>To</Label><Input required type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} className="mt-1.5" data-testid="hire-bill-date-to-input" /></div>
          <DialogFooter><Button type="submit" disabled={saving} data-testid="generate-hire-bill-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Generate</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateHireOrderDialog({ companies, vendors, equipment, projects, taxCodes, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", project_id: "", vendor_id: "", equipment_id: "", equipment_description: "", start_date: "", end_date: "", rate: "", basis: "DAILY", min_hours: "", tax_code_id: "" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/hire-orders", { ...form, project_id: form.project_id || null, equipment_id: form.equipment_id || null, end_date: form.end_date || null, min_hours: form.min_hours || null, tax_code_id: form.tax_code_id || null });
      toast.success("Hire order created");
      setForm({ company_id: "", project_id: "", vendor_id: "", equipment_id: "", equipment_description: "", start_date: "", end_date: "", rate: "", basis: "DAILY", min_hours: "", tax_code_id: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-hire-order-button"><Plus className="h-4 w-4" /> New Hire Order</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Hire Order</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-hire-order-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="hire-order-company-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vendor</Label>
              <Select required value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="hire-order-vendor-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Project <span className="font-normal text-muted-foreground">(the site the machine is hired for)</span></Label>
            <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="mt-1.5 mb-3" data-testid="hire-project-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Not for one project</SelectItem>
                {(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Label>Equipment (optional — required to generate a bill from hours)</Label>
            <Select value={form.equipment_id || "__none__"} onValueChange={(v) => setForm({ ...form, equipment_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="hire-order-equipment-select"><SelectValue placeholder="Not linked" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Not linked</SelectItem>
                {equipment.map((eq) => <SelectItem key={eq.id} value={eq.id}>{eq.code} — {eq.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Equipment description</Label><Input required value={form.equipment_description} onChange={set("equipment_description")} className="mt-1.5" data-testid="hire-order-description-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start date</Label><Input required type="date" value={form.start_date} onChange={set("start_date")} className="mt-1.5" data-testid="hire-order-start-date-input" /></div>
            <div><Label>End date</Label><Input type="date" value={form.end_date} onChange={set("end_date")} className="mt-1.5" data-testid="hire-order-end-date-input" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Rate</Label><Input required type="number" step="0.01" value={form.rate} onChange={set("rate")} className="mt-1.5" data-testid="hire-order-rate-input" /></div>
            <div>
              <Label>Basis</Label>
              <Select value={form.basis} onValueChange={(v) => setForm({ ...form, basis: v })}>
                <SelectTrigger className="mt-1.5" data-testid="hire-order-basis-select"><SelectValue /></SelectTrigger>
                <SelectContent>{BASIS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Min hours</Label><Input type="number" step="0.5" value={form.min_hours} onChange={set("min_hours")} className="mt-1.5" data-testid="hire-order-min-hours-input" /></div>
            <div>
              <Label>GST on hire bills</Label>
              <Select value={form.tax_code_id || "none"} onValueChange={(v) => setForm({ ...form, tax_code_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No GST</SelectItem>
                  {(taxCodes || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.code} ({Number(t.rate)}%)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-hire-order-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
